import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface Props {
  orgSlug: string;
  projectSlug: string;
  rowId: string;
}

/**
 * Shown after the worker confirms DNS resolves to the platform but before
 * cert issuance starts. Gives the user an explicit moment of "yes, my DNS
 * is set up correctly" feedback and a manual Complete click to trigger
 * the Let's Encrypt cert order — so we don't surprise customers with
 * background cert work they didn't ask for.
 */
export function DnsVerifiedStep({ orgSlug, projectSlug, rowId }: Props) {
  const qc = useQueryClient();
  const issue = useMutation({
    mutationFn: () =>
      api.post(`/orgs/${orgSlug}/projects/${projectSlug}/domains/${rowId}/issue`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-domains", orgSlug, projectSlug] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 text-xs rounded-md border border-success/30 bg-success-subtle/40 p-3">
        <CheckCircle2 className="h-4 w-4 mt-0.5 text-success flex-shrink-0" />
        <div className="space-y-1">
          <div className="font-semibold">DNS verified</div>
          <div className="text-muted-foreground">
            Your hostname resolves to the platform. Click Complete to issue a TLS certificate via
            Let's Encrypt — usually finishes in 5–30 seconds.
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          disabled={issue.isPending}
          onClick={() => issue.mutate()}
        >
          {issue.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Complete
        </Button>
      </div>
    </div>
  );
}
